import { AddressBookError, emptyAddressBook, keypairFromSecret, openAddressBook, sealAddressBook, type AddressBook } from "@timecapsule/crypto";
import { api } from "./api";
import { oldKeys } from "./keyring";

/**
 * The address book across key changes (reviews/06-action-plan.md 2.6). The book is sealed to one
 * key. After Reset key it must move to the new key in the same retryable step as the keyring, and
 * a book that was left behind is opened with an earlier key from the keyring and moved then. The
 * stored ciphertext is never replaced by an empty book while some key could still open it.
 */

function openWithAny(blob: string, keys: Uint8Array[]): { book: AddressBook; key: Uint8Array } | null {
  for (const key of keys) {
    try {
      return { book: openAddressBook(blob, key), key };
    } catch (err) {
      if (!(err instanceof AddressBookError)) throw err;
    }
  }
  return null;
}

const samePubkey = (a: Uint8Array, b: Uint8Array) => keypairFromSecret(a).publicKey === keypairFromSecret(b).publicKey;

/**
 * After a key reset: the book sealed to `oldSecret` (or to any key in the new keyring) is sealed to
 * `newSecret`. Repeating it after a move that succeeded changes nothing. A book no known key opens
 * is left as it is. A network error or a concurrent change is thrown; the caller offers to retry.
 */
export async function carryAddressBook(oldSecret: Uint8Array, newSecret: Uint8Array): Promise<void> {
  const stored = await api.addressBook();
  if (!stored.blob) return;
  if (openWithAny(stored.blob, [newSecret])) return;
  const opened = openWithAny(stored.blob, [oldSecret, ...(await oldKeys(newSecret)).values()]);
  if (!opened) return;
  await api.saveAddressBook(stored.version, sealAddressBook(opened.book, newSecret));
}

/**
 * Opens the stored book with the current key, else with an earlier key from the keyring; a book
 * opened with an earlier key is sealed to the current key at once. Throws AddressBookError only
 * when no known key opens it.
 */
export async function openStoredAddressBook(secretKey: Uint8Array): Promise<{ book: AddressBook; version: number }> {
  const stored = await api.addressBook();
  if (!stored.blob) return { book: emptyAddressBook(), version: stored.version };
  const opened = openWithAny(stored.blob, [secretKey, ...(await oldKeys(secretKey)).values()]);
  if (!opened) throw new AddressBookError("the address book cannot be opened with any key of this account");
  if (samePubkey(opened.key, secretKey)) return { book: opened.book, version: stored.version };
  const moved = await api.saveAddressBook(stored.version, sealAddressBook(opened.book, secretKey));
  return { book: opened.book, version: moved.version };
}
